import { Star, BadgeCheck, Users, Clock3 } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "./Reveal";
import { BRAND, CELEBS, byTitle } from "../data/site";

const STATS = [
  { icon: Star, k: `${BRAND.rating}★`, v: `${BRAND.reviewCount}+ Google reviews` },
  { icon: Users, k: "2", v: "Shops · Argyle & Lantana" },
  { icon: Clock3, k: "8-7", v: "Open every single day" },
  { icon: BadgeCheck, k: "MLB", v: "Pro athlete clientele" },
];

export const Proof = () => (
  <section
    id="proof"
    data-testid="celebrity-proof-section"
    className="relative bg-[#0a0d12] py-24 lg:py-36"
  >
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
      <SectionLabel testId="proof-label">Chapter 01 · The Proof</SectionLabel>

      <div className="mt-8 grid gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
        <div>
          <h2 className="font-cond text-4xl font-extrabold uppercase leading-[0.92] sm:text-5xl lg:text-6xl">
            <MaskLine>Celebrity barbers.</MaskLine>
            <MaskLine delay={0.1} className="brass-text">
              Neighborhood prices.
            </MaskLine>
          </h2>
          <Reveal delay={0.18}>
            <p className="mt-7 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Some of our barbers have earned "celebrity barber" status cutting
              local celebrities, professional athletes and some of the biggest
              names in North Texas. Major leaguers sit in these chairs, and so
              does{" "}
              <strong className="font-semibold text-[#e2cf9e]">Marty B</strong>{" "}
              himself, who we're right next door to in Bartonville.
            </p>
          </Reveal>

          <div className="mt-10" data-testid="celebrity-roster">
            {CELEBS.map((c, i) => (
              <Reveal key={c.name} delay={0.04 * i}>
                <div
                  data-testid={`celeb-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex items-baseline justify-between gap-4 border-b border-white/[0.08] py-4 transition-colors duration-500 hover:border-[#c5a059]/45"
                >
                  <p className="font-cond text-xl font-bold uppercase tracking-wide text-[#f2ece0] transition-colors duration-500 group-hover:text-[#e2cf9e] sm:text-2xl">
                    {c.name}
                  </p>
                  <p className="shrink-0 text-right">
                    <span className="block font-cond text-[11px] uppercase tracking-[0.18em] text-[#c5a059]">
                      {c.credit}
                    </span>
                    <span className="mt-0.5 block text-[10px] uppercase tracking-[0.16em] text-slate-500">
                      {c.role}
                    </span>
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.k} delay={0.05 * i}>
                <div className="border-t border-[#c5a059]/25 pt-4">
                  <s.icon size={15} className="mb-3 text-[#c5a059]" />
                  <p className="font-cond text-3xl font-extrabold leading-none text-[#f2ece0]">
                    {s.k}
                  </p>
                  <p className="mt-2 text-[11px] uppercase leading-tight tracking-[0.14em] text-slate-500">
                    {s.v}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* clipped editorial frames · real client photos */}
        <div className="grid grid-cols-2 gap-4">
          {[byTitle("Marty B"), byTitle("Josh Jung")].map((g, i) => (
            <Reveal key={g.title} delay={0.12 + i * 0.12}>
              <figure
                className="group relative overflow-hidden border border-white/[0.08]"
                data-testid={`proof-photo-${i}`}
              >
                <img
                  src={g.url}
                  alt={`${g.title} · ${g.tag} at The Vault Barber Co.`}
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="500"
                  className="aspect-[4/5] w-full object-cover object-center grayscale-[0.25] transition-all duration-[900ms] ease-out group-hover:scale-[1.06] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d12] via-transparent to-transparent opacity-85" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4">
                  <span className="label text-[10px]">{g.tag}</span>
                  <p className="mt-1 font-cond text-lg font-bold uppercase tracking-wide text-[#f2ece0]">
                    {g.title}
                  </p>
                </figcaption>
                <span className="absolute right-3 top-3 h-6 w-6 border-r border-t border-[#c5a059]/60" />
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
